import { GlobalConfigType } from 'actions'
import cloneDeep from 'clone-deep'
import { type InputType, COMPANY_TITLE, IS_COMPANY_CC, loanOptionsConstants } from 'config'
import { store } from 'reducers'

import { CCOptions, IntegrationType, integrationTypeTitles, OrderAppraisalOrderTypeOptions } from './types'

export const entityBorrowerTypes = ['LLC', 'LLP', 'Corp', 'Partnership']

export const isShowEntityName = (borrowerType: string, amc: string) =>
  entityBorrowerTypes.includes(borrowerType) &&
  [
    integrationTypeTitles[IntegrationType.VisionVMC],
    integrationTypeTitles[IntegrationType.AMCDirect],
    'Trinity',
  ].includes(amc)

export const defaultInputs = (): Record<string, InputType> => {
  const { globalConfigReducer } = store.getState()
  const orderAppraisalVendorOptions = globalConfigReducer[GlobalConfigType.ORDER_VALUATION]

  const inputs: Record<string, InputType> = {
    // File / Company Information
    dividerCompany: {
      inputType: 'divider',
      title: 'File / Company Information',
    },
    companyID: {
      inputType: 'select',
      title: 'Company ID',
      value: COMPANY_TITLE,
      options: [COMPANY_TITLE],
      hasDefaultOption: true,
      error: '',
      required: true,
    },
    loanNumber: {
      inputType: 'text',
      title: 'Loan Number',
      value: '',
      error: '',
      required: true,
    },
    loanOfficerEmail: {
      inputType: 'text',
      type: 'email',
      title: 'Loan Officer Email',
      required: true,
      copyable: true,
    },
    processorEmail: {
      inputType: 'text',
      type: 'email',
      title: 'Processor Email',
    },

    // Associated Dates
    dividerDates: {
      inputType: 'divider',
      title: 'Associated Dates',
    },
    orderDate: {
      inputType: 'text',
      type: 'date',
      title: 'Order Date',
      required: true,
    },
    clientDueDate: {
      inputType: 'text',
      type: 'date',
      title: 'Client Due Date',
      required: true,
    },
    inspectionDate: {
      inputType: 'text',
      type: 'date',
      title: 'Inspection Date',
    },
    acceptedDate: {
      inputType: 'text',
      type: 'date',
      title: 'Accepted Date',
    },
    receivedByBorrowerDate: {
      inputType: 'text',
      type: 'date',
      title: 'Received by Borrower',
      visible: !IS_COMPANY_CC,
    },
    waiverDate: {
      inputType: 'text',
      type: 'date',
      title: 'Timing Waiver Date',
      visible: !IS_COMPANY_CC,
    },
    revisionDateRequested: {
      inputType: 'text',
      type: 'date',
      title: 'Revision date requested',
      visible: IS_COMPANY_CC,
    },
    revisionDateCompleted: {
      inputType: 'text',
      type: 'date',
      title: 'Revision date completed',
      visible: IS_COMPANY_CC,
    },
    completeDate: {
      inputType: 'text',
      type: 'date',
      title: 'Complete Date',
    },
    lenderDueDate: {
      inputType: 'text',
      type: 'date',
      title: 'Lender Due Date',
    },
    paidDate: {
      inputType: 'text',
      type: 'date',
      title: 'Paid Date',
    },
    receivedDateByLender: {
      inputType: 'text',
      type: 'date',
      title: 'Received Date by Lender',
    },
    appraisalCost: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Appraisal Cost',
      prefix: '$',
    },

    // Borrower & Contacts
    dividerBorrower: {
      inputType: 'divider',
      title: 'Borrower & Contacts',
      copyable: true,
    },
    borrowerFirstName: {
      inputType: 'text',
      title: 'Borrower First Name',
      required: true,
      copyable: true,
    },
    borrowerLastName: {
      inputType: 'text',
      title: 'Borrower Last Name',
      required: true,
      copyable: true,
    },
    entityName: {
      inputType: 'text',
      title: 'Entity Name',
      value: '',
      error: '',
      required: true,
      span: 2,
    },
    borrowerEmail: {
      inputType: 'text',
      type: 'email',
      title: 'Borrower Primary Email',
      required: true,
      copyable: true,
    },
    borrowerPhone: {
      inputType: 'text',
      type: 'phone',
      title: 'Borrower Primary Phone',
      required: true,
      copyable: true,
    },
    contactFirstName: {
      inputType: 'text',
      title: 'Contact First Name',
      required: true,
      copyable: true,
    },
    contactLastName: {
      inputType: 'text',
      title: 'Contact Last Name',
      required: true,
      copyable: true,
    },
    contactEmail: {
      inputType: 'text',
      type: 'email',
      title: 'Contact Primary Email',
      required: true,
      copyable: true,
    },
    contactPhone: {
      inputType: 'text',
      type: 'phone',
      title: 'Contact Primary Phone',
      required: true,
      copyable: true,
    },
    contactSameAsBorrower: {
      inputType: 'toggle',
      value: false,
      title: 'Contact Info Same as Borrower Info',
      span: 2,
    },
    // Property Details
    dividerProperty: {
      inputType: 'divider',
      title: 'Property Details',
    },
    propertyAddress: {
      inputType: 'AutoComplete',
      autoCompleteType: 'map',
      title: 'Property Address',
      value: [],
      error: '',
      required: true,
      span: 4,
    },
    propertyCounty: {
      inputType: 'text',
      title: 'Property County',
      value: '',
      error: '',
      span: 1,
    },
    propertyType: {
      inputType: 'select',
      title: 'Property Type',
      value: '',
      hasDefaultOption: true,
      options: loanOptionsConstants.propertyType,
      required: true,
    },
    pointOfContact: {
      inputType: 'select',
      title: 'Point of Contact',
      value: 'Borrower',
      options: {
        Contact: 'Contact Person',
        Borrower: 'Borrower',
      },
      required: true,
    },

    // Vendor
    dividerVendor: {
      inputType: 'divider',
      title: 'Vendor',
      visible: true,
    },

    amc: {
      inputType: 'select',
      title: 'Vendor',
      value: '',
      options: cloneDeep(orderAppraisalVendorOptions),
      sort: true,
      error: '',
      hasDefaultOption: true,
      required: false,
    },

    // Select Client Lender for Appraisal Scope
    dividerClientLender: {
      inputType: 'divider',
      title: 'Select Client Lender for Appraisal Scope',
      visible: false,
    },

    clientLenderAccount: {
      inputType: 'select',
      title: 'Account',
      value: '',
      hasDefaultOption: true,
      options: { main: COMPANY_TITLE, broker: 'Broker', myAccount: 'My Account' },
      required: true,
      span: 2,
      visible: false,
    },

    clientLenderCompany: {
      inputType: 'select',
      title: 'Company List',
      value: '',
      hasDefaultOption: true,
      options: [],
      required: true,
      span: 2,
      visible: false,
    },

    // Order Details
    dividerOrderDetail: {
      inputType: 'divider',
      title: 'Order Details',
    },
    orderType: {
      inputType: 'select',
      title: 'Order Type',
      value: '',
      options: OrderAppraisalOrderTypeOptions,
      error: '',
      hasDefaultOption: true,
      required: true,
      sort: true,
    },
    appraisalForm: {
      inputType: 'select',
      tooltip: 'If you select Order Type, then drop down options will exist!',
      title: 'Appraisal Form',
      value: '',
      options: [],
      hasDefaultOption: true,
      required: true,
      sort: true,
    },
    appraisalScopeJobAddons: {
      inputType: 'multiselect',
      tooltip: 'If you select Appraisal Scope, then drop down options will exist!',
      title: 'Appraisal Job Addons',
      value: {},
      options: [],
      defaultSelected: false,
      required: false,
      visible: false,
      sort: true,
    },
    valueLinkAdditionalItems: {
      inputType: 'multiselect',
      tooltip: 'Select up to 3 additional items!',
      title: 'Additional Item(s)',
      value: {},
      options: [],
      defaultSelected: false,
      required: false,
      visible: false,
      sort: true,
      maxSelected: 3,
    },
    purpose: {
      inputType: 'select',
      title: 'Loan Purpose',
      value: '',
      options: ['Purchase', 'Refinance'],
      hasDefaultOption: true,
      required: true,
    },
    purchaseAmount: {
      inputType: 'text',
      title: 'Purchase Amount',
      prefix: '$',
      value: '',
      type: 'thousandSep',
      error: '',
      required: true,
    },
    occupancy: {
      inputType: 'select',
      title: 'Occupancy',
      value: '',
      options: ['Investment', 'Primary', 'Secondary'],
      hasDefaultOption: true,
      required: true,
    },
    rush: {
      inputType: 'check',
      title: 'Rush Order',
      value: false,
      error: '',
      span: 2,
    },
    refreshMessages: {
      inputType: 'check',
      title: 'Refresh Messages',
      value: false,
      error: '',
      span: 2,
      visible: false,
      alwaysEditable: true,
    },
    noteMessages: {
      inputType: 'textarea',
      title: 'Note Messages',
      value: '',
      rows: 2,
      span: 2,
      history: true,
      // AMC Direct sends this as AccessInformation. inputLogic marks it required for that vendor.
      tooltip: 'Required for AMC Direct. Sent to the vendor as property access information.',
    },
    trinityOrderID: {
      inputType: 'text',
      title: 'Trinity Order ID',
      value: '',
      type: 'number',
      error: '',
    },
    notifications: {
      inputType: 'autocomplete',
      title: 'Notification Emails',
      type: 'email',
      required: true,
      span: 2,
      value: [],
    },
    underConstruction: {
      inputType: 'check',
      title: 'Is property currently under construction?',
      value: '',
      error: '',
      span: 3,
      visible: false,
      required: true,
    },

    // Feasibility Authorization Method
    feasibilityTitle: {
      inputType: 'divider',
      title: 'Feasibility Payment Method',
      visible: false,
    },
    feasibilityType: {
      inputType: 'select',
      title: 'Type',
      value: '',
      options: ['ACH', 'Credit Card'],
      hasDefaultOption: true,
      required: true,
      visible: false,
    },
    feasibilitySignerName: {
      inputType: 'text',
      type: 'text',
      title: 'Signer Name',
      required: true,
      visible: false,
    },
    feasibilityEmailToLink: {
      inputType: 'text',
      type: 'email',
      title: 'Email to Send Link',
      required: true,
      visible: false,
      span: 2,
    },

    feasibilityACHPaymentAmount: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Payment Amount',
      prefix: '$',
      required: true,
      visible: false,
      disabled: true,
    },
    feasibilityACHFinancialInstitution: {
      inputType: 'text',
      type: 'text',
      title: 'Name of Financial Institution',
      required: true,
      visible: false,
    },
    feasibilityACHAccount: {
      inputType: 'text',
      type: 'text',
      title: 'Name on Account',
      required: true,
      visible: false,
    },
    feasibilityACHRoutingNumber: {
      inputType: 'text',
      type: 'routingNo',
      title: 'Routing Number',
      maxLength: 9,
      required: true,
      visible: false,
    },
    feasibilityACHAccountNumber: {
      inputType: 'text',
      type: 'bankAccountNo',
      title: 'Account Number',
      minLength: 8,
      maxLength: 17,
      required: true,
      visible: false,
    },
    feasibilityACHAccountType: {
      inputType: 'select',
      title: 'Account Type',
      options: ['Checking', 'Saving'],
      hasDefaultOption: true,
      required: true,
      visible: false,
    },
    feasibilityCCType: {
      inputType: 'select',
      title: 'CC Type',
      value: '',
      hasDefaultOption: true,
      options: CCOptions,
      required: true,
      visible: false,
    },

    feasibilityCCStudyFee: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Payment Amount',
      prefix: '$',
      required: true,
      disabled: true,
      visible: false,
    },
    feasibilityCCFirstName: {
      inputType: 'text',
      title: 'First Name',
      value: '',
      error: '',
      required: true,
      visible: false,
    },
    feasibilityCCLastName: {
      inputType: 'text',
      title: 'Last Name',
      value: '',
      error: '',
      required: true,
      visible: false,
    },
    feasibilityCCCardNumber: {
      inputType: 'text',
      title: 'Card Number',
      value: '',
      error: '',
      required: true,
      visible: false,
    },
    feasibilityCCCardCode: {
      inputType: 'text',
      title: 'Card Code',
      value: '',
      error: '',
      required: true,
      visible: false,
    },
    feasibilityCCExpMonth: {
      inputType: 'select',
      title: 'Exp Month',
      value: '',
      hasDefaultOption: true,
      options: ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'],
      required: true,
      visible: false,
    },
    feasibilityCCExpYear: {
      inputType: 'select',
      title: 'Exp Year',
      value: '',
      hasDefaultOption: true,
      options: ['2020', '2021', '2022', '2023', '2024', '2025', '2026', '2027', '2028', '2029', '2030'],
      required: true,
      visible: false,
    },
    feasibilityCCBillingAddress: {
      inputType: 'map',
      title: 'Billing Address',
      value: '',
      error: '',
      required: true,
      visible: false,
      span: 3,
    },
    feasibilitySignKey: {
      inputType: 'custom',
      title: '',
      visible: false,
      span: 2,
    },

    // AMC Direct deferred payment (Pending Payment only)
    amcDirectPaymentActions: {
      inputType: 'custom',
      title: '',
      visible: false,
      span: 2,
    },
    amcDirectPaymentLink: {
      inputType: 'text',
      title: 'Payment Link',
      value: '',
      visible: false,
      span: 2,
      copyable: true,
      disabled: true,
    },

    // Payment Information
    dividerPay: {
      inputType: 'divider',
      title: 'Payment Information',
    },
    isSendInvoice: {
      inputType: 'check',
      title: 'Send Payment Link',
      value: true,
      error: '',
      span: 2,
      required: true,
    },
    paymentLinkEmail: {
      inputType: 'AutoComplete',
      type: 'email',
      title: 'Emails to Send Link',
      value: [],
      required: true,
      span: 2,
    },
    ccType: {
      inputType: 'select',
      title: 'CC Type',
      value: '',
      hasDefaultOption: true,
      options: CCOptions,
      required: true,
      visible: false,
    },
    cardFirstName: {
      inputType: 'text',
      title: 'First Name',
      value: '',
      error: '',
      required: true,
      visible: false,
    },
    cardLastName: {
      inputType: 'text',
      title: 'Last Name',
      value: '',
      error: '',
      required: true,
      visible: false,
    },
    cardEmail: {
      inputType: 'text',
      title: 'Email',
      type: 'email',
      value: '',
      error: '',
      required: true,
      visible: false,
    },
    paymentAmount: {
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Payment Amount',
      value: '',
      error: '',
      visible: false,
    },
    cardNumber: {
      inputType: 'text',
      title: 'Card Number',
      value: '',
      error: '',
      required: true,
      visible: false,
    },
    cardCode: {
      inputType: 'text',
      title: 'Card Code',
      value: '',
      error: '',
      required: true,
      visible: false,
    },
    expMonth: {
      inputType: 'select',
      title: 'Exp Month',
      value: '',
      hasDefaultOption: true,
      options: ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'],
      required: true,
      visible: false,
    },
    expYear: {
      inputType: 'select',
      title: 'Exp Year',
      value: '',
      hasDefaultOption: true,
      options: ['2020', '2021', '2022', '2023', '2024', '2025', '2026', '2027', '2028', '2029', '2030'],
      required: true,
      visible: false,
    },
    billingAddress: {
      inputType: 'map',
      title: 'Billing Address',
      value: '',
      error: '',
      required: true,
      visible: false,
      span: 3,
    },

    dividerDocuments: {
      inputType: 'divider',
      title: 'Documents',
    },
    underConstructionAlert: {
      inputType: 'alert',
      title: '',
      value: 'Please upload Budget and Scope of Work!',
      visible: false,
      span: 4,
    },
    documents: {
      inputType: 'custom',
      title: '',
      span: 4,
    },
  }

  Object.values(inputs).forEach((input) => {
    if (['divider', 'section', 'alert', 'custom'].includes(input.inputType)) return
    input.history = true
  })

  return inputs
}
